import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import {
  TarefaAtribuidoFiltrosCriteria,
  TarefaCriadorFiltrosCriteria,
  TarefaFiltrosCriteria,
  TarefaLogFiltrosCriteria,
  TarefaObservadaFiltrosCriteria,
  TarefaStatus,
  TarefaTipo,
} from "@workflow/types";

import { tarefaService } from "@/services/api/tarefa.service";
import { tarefaAuditoriaService } from "@/services/api/TarefaAuditoriaService";

const MINUTE = 1000 * 60;
const LIST_STALE = 30 * 1000;
const FILTROS_STALE = 30 * MINUTE;

/**
 * Bases de query key do domínio "tarefas" — **alinhadas às keys que
 * `useTarefaActions` já invalida** (cada visão é uma base distinta, não aninhada).
 * Os list keys são prefixados pela base correspondente, então a invalidação
 * existente os alcança por prefixo.
 */
export const tarefasKeys = {
  admin: ["tarefas"] as const,
  atribuidas: ["tarefas-atribuidas"] as const,
  criadas: ["tarefas-criadas"] as const,
  observadas: ["tarefas-observadas"] as const,
  /** Base da página "Públicos" (tarefas públicas dos jobs acessíveis). */
  publicas: ["tarefas-publicas"] as const,
  /** Base da aba "Histórico" em `TarefaAdminListPage` (auditoria admin-wide). */
  logsAdmin: ["tarefas-logs-admin"] as const,
  /** Base por Job (aba "Tarefas do Job"). */
  porJob: (jobId: number) => ["tarefas-job", jobId] as const,
  /**
   * Preview de criação de tarefa (server-side). Unifica os literais soltos que
   * cada hook de criação usava; `deps` são os ids que compõem a chave + as
   * observações debounced.
   */
  preview: (tipo: TarefaTipo, deps: readonly unknown[] = []) =>
    ["tarefa-preview", tipo, ...deps] as const,
  /** Todas as bases de lista — para updates otimistas que varrem as 4 visões. */
  allLists: () =>
    [["tarefas"], ["tarefas-atribuidas"], ["tarefas-criadas"], ["tarefas-observadas"]] as const,
};

export const tarefasQueries = {
  listAdmin: (criteria: TarefaFiltrosCriteria) =>
    queryOptions({
      queryKey: [...tarefasKeys.admin, "list", criteria],
      queryFn: () => tarefaService.listTarefas(criteria),
      placeholderData: keepPreviousData,
      staleTime: LIST_STALE,
    }),
  filtrosAdmin: () =>
    queryOptions({
      queryKey: [...tarefasKeys.admin, "filtros"],
      queryFn: () => tarefaService.getFiltrosTarefa(),
      staleTime: FILTROS_STALE,
    }),

  listAtribuidas: (criteria: TarefaAtribuidoFiltrosCriteria) =>
    queryOptions({
      queryKey: [...tarefasKeys.atribuidas, "list", criteria],
      queryFn: () => tarefaService.listTarefasAtribuidasAMim(criteria),
      placeholderData: keepPreviousData,
      staleTime: LIST_STALE,
    }),
  filtrosAtribuidas: () =>
    queryOptions({
      queryKey: [...tarefasKeys.atribuidas, "filtros"],
      queryFn: () => tarefaService.getFiltrosTarefaAtribuidasAMim(),
      staleTime: FILTROS_STALE,
    }),

  /** Aba "Atrasadas": mesma listagem de atribuídas, forçando `apenasAtrasadas`. */
  listAtribuidasAtrasadas: (criteria: TarefaAtribuidoFiltrosCriteria) =>
    queryOptions({
      queryKey: [...tarefasKeys.atribuidas, "atrasadas", "list", criteria],
      queryFn: () =>
        tarefaService.listTarefasAtribuidasAMim({ ...criteria, apenasAtrasadas: true }),
      placeholderData: keepPreviousData,
      staleTime: LIST_STALE,
    }),

  listCriadas: (criteria: TarefaCriadorFiltrosCriteria) =>
    queryOptions({
      queryKey: [...tarefasKeys.criadas, "list", criteria],
      queryFn: () => tarefaService.listTarefasCriadasPorMim(criteria),
      placeholderData: keepPreviousData,
      staleTime: LIST_STALE,
    }),
  filtrosCriadas: () =>
    queryOptions({
      queryKey: [...tarefasKeys.criadas, "filtros"],
      queryFn: () => tarefaService.getFiltrosTarefaCriadasPorMim(),
      staleTime: FILTROS_STALE,
    }),

  listObservadas: (criteria: TarefaObservadaFiltrosCriteria) =>
    queryOptions({
      queryKey: [...tarefasKeys.observadas, "list", criteria],
      queryFn: () => tarefaService.listTarefasObservadasPorMim(criteria),
      placeholderData: keepPreviousData,
      staleTime: LIST_STALE,
    }),

  /** Listagem paginada das tarefas públicas dos jobs a que o usuário tem acesso. */
  listPublicas: (criteria: TarefaFiltrosCriteria) =>
    queryOptions({
      queryKey: [...tarefasKeys.publicas, "list", criteria],
      queryFn: () => tarefaService.listTarefasPublicas(criteria),
      placeholderData: keepPreviousData,
      staleTime: LIST_STALE,
    }),

  /** Listagem paginada das tarefas de um Job (qualquer status), server-driven. */
  listPorJob: (jobId: number) => (criteria: TarefaFiltrosCriteria) =>
    queryOptions({
      queryKey: [...tarefasKeys.porJob(jobId), "list", criteria],
      queryFn: () => tarefaService.listTarefasPorJobPaginado(jobId, criteria),
      placeholderData: keepPreviousData,
      staleTime: LIST_STALE,
    }),
  filtrosObservadas: () =>
    queryOptions({
      queryKey: [...tarefasKeys.observadas, "filtros"],
      queryFn: () => tarefaService.getFiltrosTarefaObservadasPorMim(),
      staleTime: FILTROS_STALE,
    }),

  /** Histórico de eventos de tarefa, admin-wide (aba "Histórico"). */
  logsAdminList: (criteria: TarefaLogFiltrosCriteria) =>
    queryOptions({
      queryKey: [...tarefasKeys.logsAdmin, "list", criteria],
      queryFn: () => tarefaAuditoriaService.listLogsPaginado(criteria),
      placeholderData: keepPreviousData,
      staleTime: LIST_STALE,
    }),
  logsAdminFiltros: () =>
    queryOptions({
      queryKey: [...tarefasKeys.logsAdmin, "filtros"],
      queryFn: () => tarefaAuditoriaService.getLogFiltros(),
      staleTime: FILTROS_STALE,
    }),
};
